import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography, alpha } from "@mui/material";
import {
  Spa as SpaIcon,
  AssignmentTurnedIn as ConsentIcon,
  PictureAsPdf as PdfIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";

import ActionButton from "../../atoms/ActionButton";
import SelectableListRow from "../../molecules/SelectableListRow";
import { RowLine, RowChip, RowMetaText } from "./_rowParts";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
} from "./_workspaceParts";
import { EDITOR_PX } from "./_editorParts";
import {
  PracticeSessionDetail,
  PracticeSessionDrawer,
  ConsentRecordDrawer,
  ConsentListDrawer,
  PendingConsentBanner,
  SESSION_STATUS,
  formatDateTr,
  personName,
} from "../PracticeSession";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  useListPracticeSessionsByPatientMutation,
  useListPatientConsentsMutation,
  useListTraditionalPracticesMutation,
} from "../../../store/traditionalApi";
import useTerms from "../../../hooks/useTerms";

// @react-pdf + fontlar ayrı chunk (bkz. TreatmentEditor)
const ConsentPreview = lazy(() => import("../pdf/ConsentPreview"));

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || null;

/**
 * Hasta › Uygulamalar — geleneksel uygulama seansları (Faz 5).
 * Sol: seans listesi; sağ: seçili seansın salt-okunur görünümü (vücut haritası
 * dahil). Yeni seans / düzenleme sağdan drawer'da. Araç satırında onam listesi.
 * Kurallar: docs/design-principles.md §8.3
 */
export default function PracticeSessionsTab({ patient, patientId, clinicId, token, canEdit = true }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showError, showSuccess, showWarning } = useAlert();
  const canCreate = useHasPermission("sessions:create") && canEdit;
  const canUpdate = useHasPermission("sessions:update") && canEdit;
  const canReadConsents = useHasPermission("consents:read");
  const canManageConsent = useHasPermission("consents:manage") && canEdit;
  const scope = useMemo(() => (clinicId ? { clinicId } : {}), [clinicId]);
  const patientName = `${patient?.name || ""} ${patient?.surname || ""}`.trim();
  // Uygulamaya giriş yapmış hastada her seansın onayı telefona gider ("Onay bekliyor")
  const patientUsesApp = patient?.hasLoggedIn !== false;

  const [listSessions, { isLoading: loadingList }] = useListPracticeSessionsByPatientMutation();
  const [listConsents] = useListPatientConsentsMutation();
  const [listPractices] = useListTraditionalPracticesMutation();

  const [sessions, setSessions] = useState([]);
  const [consents, setConsents] = useState([]);
  const [practices, setPractices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editor, setEditor] = useState({ open: false, session: null });
  const [consentListOpen, setConsentListOpen] = useState(false);
  const [consentNewOpen, setConsentNewOpen] = useState(false);
  const [printRecord, setPrintRecord] = useState(null);

  const refreshSessions = useCallback(async () => {
    if (!token || !patientId) return;
    try {
      const res = await listSessions({ token, patientId, ...scope }).unwrap();
      const list = res?.data?.list || [];
      setSessions(list);
      setSelectedId((cur) => cur || list[0]?._id || null);
    } catch (_) {
      showError("Seanslar yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, patientId, scope, listSessions, showError]);

  const refreshConsents = useCallback(async () => {
    if (!token || !patientId || !canReadConsents) return;
    try {
      const res = await listConsents({ token, patientId, ...scope }).unwrap();
      setConsents(res?.data?.list || []);
    } catch (_) {
      // onam listesi ikincil — sessizce boş kalır
    }
  }, [token, patientId, scope, canReadConsents, listConsents]);

  useEffect(() => {
    refreshSessions();
    refreshConsents();
  }, [refreshSessions, refreshConsents]);

  useEffect(() => {
    if (!token) return;
    listPractices({ token, status: "active", limit: 200 })
      .unwrap()
      .then((res) => setPractices(res?.data?.items || []))
      .catch(() => setPractices([]));
  }, [token, listPractices]);

  const selected = useMemo(() => sessions.find((s) => s._id === selectedId) || null, [sessions, selectedId]);
  const selectedConsent = useMemo(
    () => (selected ? consents.find((c) => c._id === idOf(selected.consent)) || null : null),
    [selected, consents]
  );

  const upsertSession = useCallback((saved) => {
    if (!saved?._id) return;
    setSessions((prev) => {
      const exists = prev.some((s) => s._id === saved._id);
      const next = exists ? prev.map((s) => (s._id === saved._id ? saved : s)) : [saved, ...prev];
      return next.sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt));
    });
  }, []);

  // Onay bekleyen seansta yeniden gönder / kağıtla onayla / iptal sonrası: satırı güncelle,
  // kağıt onam yeni kayıt açtığından onam listesini de tazele
  const handlePendingChanged = (updated) => {
    upsertSession(updated);
    refreshConsents();
  };

  const handleSaved = (saved, warnings) => {
    setEditor({ open: false, session: null });
    if (!saved?._id) return;
    upsertSession(saved);
    setSelectedId(saved._id);
    if (warnings?.length) showWarning(warnings.join(" "));
    else showSuccess(editor.session ? "Seans güncellendi." : "Seans kaydedildi.");
  };

  const openNew = () => setEditor({ open: true, session: null });
  const showLoading = !loaded && loadingList;

  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {canReadConsents ? (
        <Button
          size="small"
          startIcon={<ConsentIcon sx={{ fontSize: 16 }} />}
          onClick={() => setConsentListOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: TYPO.label,
            color: COLORS.slate600,
            border: `1px solid ${COLORS.slate200}`,
            borderRadius: 999,
            px: 1.5,
          }}
        >
          Onamlar · {consents.length}
        </Button>
      ) : null}
      {canCreate ? (
        <ActionButton variant="add" compact palette={palette} onClick={openNew} label="Yeni Seans" aria-label="Yeni seans ekle" />
      ) : null}
    </Box>
  );

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody title="Uygulamalar" count={sessions.length} action={toolbar}>
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : sessions.length === 0 ? (
          <WorkspaceEmpty
            icon={<SpaIcon />}
            title="Henüz seans yok"
            lead={`${terms.patientDat} yapılan ilk uygulamayı`}
            linkLabel="kaydedin"
            tail="Uygulama türü, vücut haritası, gözlem ve öneriler tek kayıtta tutulur."
            readOnlyText={`Bu ${terms.patientLower} için kaydedilmiş uygulama seansı bulunmuyor.`}
            onCreate={canCreate ? openNew : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                {sessions.map((s) => {
                  const cancelled = s.status === "cancelled";
                  const pending = s.status === "pending_consent";
                  return (
                    <SelectableListRow
                      key={s._id}
                      active={s._id === selectedId}
                      onClick={() => setSelectedId(s._id)}
                      sx={LIST_ROW_SX}
                      title={s.practiceName}
                      subtitle={
                        <RowLine flex sx={{ fontSize: "0.78rem", color: COLORS.slate500 }}>
                          {personName(s.practitioner || s.createdBy) || "—"}
                          {s.regions?.length ? ` · ${s.regions.length} bölge` : ""}
                          {cancelled ? (
                            <RowChip label={SESSION_STATUS.cancelled.label} sx={{ bgcolor: COLORS.slate100, color: COLORS.slate600 }} />
                          ) : null}
                          {pending ? (
                            <RowChip
                              label={SESSION_STATUS.pending_consent.label}
                              sx={{ bgcolor: COLORS.warningLight, color: COLORS.warningDarker }}
                            />
                          ) : null}
                          {(s.cautionsAcknowledged || []).length ? (
                            <RowChip
                              label="Uyarı"
                              sx={{ bgcolor: alpha(COLORS.warning, 0.14), color: COLORS.warningDarker }}
                            />
                          ) : null}
                        </RowLine>
                      }
                      trailing={<RowMetaText>{formatDateTr(s.performedAt)}</RowMetaText>}
                    />
                  );
                })}
              </Stack>
            }
            editor={
              !selected ? (
                <SelectPrompt icon={<SpaIcon />} title="Bir seans seçin" description="Ayrıntıları görmek için soldaki listeden bir seans seçin." />
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  <Box
                    sx={{
                      ...EDITOR_PX,
                      py: 1.25,
                      minHeight: 56,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderBottom: `1px solid ${COLORS.slate100}`,
                    }}
                  >
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate700 }} noWrap>
                      {selected.practiceName}
                    </Typography>
                    {canUpdate ? (
                      <ActionButton
                        variant="edit"
                        compact
                        palette={palette}
                        icon={<EditIcon sx={{ fontSize: 16 }} />}
                        label="Düzenle"
                        onClick={() => setEditor({ open: true, session: selected })}
                      />
                    ) : null}
                  </Box>
                  <PendingConsentBanner
                    session={selected}
                    token={token}
                    clinicId={clinicId}
                    patientName={patientName}
                    canUpdate={canUpdate}
                    canManageConsent={canManageConsent}
                    onChanged={handlePendingChanged}
                  />
                  <PracticeSessionDetail
                    session={selected}
                    consentAction={
                      selectedConsent ? (
                        <Button
                          size="small"
                          startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setPrintRecord(selectedConsent)}
                          sx={{ textTransform: "none", fontWeight: 600, fontSize: TYPO.label, color: palette?.primary, px: 1 }}
                        >
                          PDF
                        </Button>
                      ) : null
                    }
                  />
                </Box>
              )
            }
          />
        )}
      </WorkspaceBody>

      <PracticeSessionDrawer
        open={editor.open}
        onClose={() => setEditor({ open: false, session: null })}
        token={token}
        patientId={patientId}
        clinicId={clinicId}
        patientName={patientName}
        session={editor.session}
        practices={practices}
        canManageConsent={canManageConsent}
        patientUsesApp={patientUsesApp}
        onSaved={handleSaved}
        onConsentRecorded={refreshConsents}
      />

      <ConsentListDrawer
        open={consentListOpen}
        onClose={() => setConsentListOpen(false)}
        records={consents}
        patientName={patientName}
        onPrint={setPrintRecord}
        onRecordNew={canManageConsent ? () => setConsentNewOpen(true) : undefined}
      />

      <ConsentRecordDrawer
        open={consentNewOpen}
        onClose={() => setConsentNewOpen(false)}
        token={token}
        patientId={patientId}
        clinicId={clinicId}
        patientName={patientName}
        onSaved={refreshConsents}
      />

      {printRecord && (
        <Suspense fallback={null}>
          <ConsentPreview open={Boolean(printRecord)} onClose={() => setPrintRecord(null)} patient={patient} record={printRecord} />
        </Suspense>
      )}
    </Box>
  );
}
